import { checkIsObjEmpty } from '@repo/internal-tools-ts-utils/src/lib/checkIsObjEmpty.ts'
import type { UnknownObj } from '@repo/internal-tools-type-utils/src/primitives.ts'
import type { QueryKey } from '@tanstack/react-query'
import createClient from 'openapi-fetch'
import type {
  HasRequiredKeys,
  RequestBodyJSON,
} from 'openapi-typescript-helpers'
import { HTTP_STATUS } from '../constants/HttpStatus'
import {
  type ErrorResponseData,
  InternalServerError,
  NotFoundError,
  UnauthorizedError,
  UserInfoAccessError,
} from '../types/Errors'
import type { paths } from '../types/generated/ccc-backend.types.ts'
import type {
  DeleteApiResponse,
  DeleteEndpoints,
  GetApiResponse,
  GetEndpoints,
  GetQueryParams,
  PatchApiResponse,
  PatchEndpoints,
  PostApiResponse,
  PostEndpoints,
  PostQueryParams,
  PostRequestBody,
  PutApiResponse,
  PutEndpoints,
  PutQueryParams,
  PutRequestBody,
} from '../types/OpenApiTypes'
import type { IApiService } from './IApiService.ts'

export type CreateRequestKeyOptions = {
  keepEmptyKeys?: boolean
}

export abstract class BaseCccBackendApiService<
  TVariables,
  TResponseData,
  TErrorResponseData extends Pick<
    ErrorResponseData,
    'code' | 'message' | 'errors'
  > = ErrorResponseData,
  TQueryKey extends QueryKey = QueryKey,
> implements
    IApiService<TVariables, TResponseData, TErrorResponseData, TQueryKey>
{
  abstract readonly requestKeyNamespace: string
  readonly baseApiUrl: string
  private client: ReturnType<typeof createClient<paths>>

  constructor(baseUrl: string) {
    this.baseApiUrl = baseUrl
    this.client = createClient<paths>({
      baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
  }

  abstract getRequestKey(variables?: NoInfer<Partial<TVariables>>): TQueryKey

  abstract request(variables: TVariables): Promise<TResponseData>

  abstract $mock(
    variables: NoInfer<TVariables>,
  ): Promise<NoInfer<TResponseData> | void>

  onError(_errorResponseData: TErrorResponseData): void {}

  createRequestKey<K = never>(
    keys: K[] = [],
    options: CreateRequestKeyOptions = {},
  ): (string | K)[] {
    const { keepEmptyKeys = false } = options

    let requestKeys = [this.requestKeyNamespace, ...keys]

    if (!keepEmptyKeys) {
      // Remove nullish keys. When we get the request key for invalidating caches,
      // it will be consistent and caches would be removed correctly.
      // The order in the array matters, but order of keys in an object don't.
      // @see https://tanstack.com/query/v4/docs/framework/react/guides/query-keys#query-keys-are-hashed-deterministically
      requestKeys = requestKeys.filter((key) => {
        // null is allowed, but undefined will be removed
        if (key === null) return true
        if (key === undefined) return false
        // Remove empty array
        if (Array.isArray(key)) return key.length > 0
        // Remove empty object
        if (
          typeof key === 'object' &&
          checkIsObjEmpty(key as UnknownObj, { ignoreUndefined: true })
        ) {
          return false
        }

        // default
        return true
      })
    }

    return requestKeys.map((key) => {
      // Convert all numbers into string to improve cache hit rate
      // when some keys are numbers and some are strings from different usages.
      // This is because in some request, we allow sending path and query params loosely
      // by either their true type or string type.
      if (typeof key === 'number') return String(key)
      return key
    })
  }

  /**
   * Request GET endpoint
   * @param endpoint Endpoint to call
   * @param args Request params (path/querystring)
   * @returns Response from endpoint
   */
  public async get<TEndpoint extends GetEndpoints>(
    endpoint: TEndpoint,
    ...args: HasRequiredKeys<GetQueryParams<TEndpoint>> extends never
      ? [GetQueryParams<TEndpoint>?]
      : [GetQueryParams<TEndpoint>]
  ): Promise<GetApiResponse<TEndpoint>> {
    const { parseAs, ...params } = args[0] ?? {}
    const response = await this.client.GET<GetEndpoints>(endpoint, {
      params,
      parseAs,
    })

    if (response.error) {
      const { response: httpResponse, error } =
        response as GetApiResponse<TEndpoint>
      this.handleErrorResponse(httpResponse, error as TErrorResponseData)

      return { error, response: httpResponse } as GetApiResponse<TEndpoint>
    }

    return response
  }

  /**
   * Request POST endpoint
   * @param endpoint Endpoint to call
   * @param payload Request Payload
   * @param args Request params (path/querystring)
   * @returns Response from endpoint
   */
  public async post<TEndpoint extends PostEndpoints>(
    endpoint: TEndpoint,
    payload: PostRequestBody<TEndpoint>,
    ...args: HasRequiredKeys<PostQueryParams<TEndpoint>> extends never
      ? [PostQueryParams<TEndpoint>?]
      : [PostQueryParams<TEndpoint>]
  ): Promise<PostApiResponse<TEndpoint>> {
    const { bodySerializer, parseAs, ...params } = args[0] ?? {}
    const response = await this.client.POST<PostEndpoints>(endpoint, {
      body: payload as RequestBodyJSON<TEndpoint>,
      params,
      bodySerializer,
      parseAs,
    })

    if (response.error) {
      const { response: httpResponse, error } =
        response as PostApiResponse<TEndpoint>
      this.handleErrorResponse(httpResponse, error as TErrorResponseData)

      return { error, response: httpResponse } as PostApiResponse<TEndpoint>
    }

    return response
  }

  public async put<TEndpoint extends PutEndpoints>(
    endpoint: TEndpoint,
    payload: PutRequestBody<TEndpoint>,
    ...args: HasRequiredKeys<PutQueryParams<TEndpoint>> extends never
      ? [PutQueryParams<TEndpoint>?]
      : [PutQueryParams<TEndpoint>]
  ): Promise<PutApiResponse<TEndpoint>> {
    const response = await this.client.PUT<PutEndpoints>(endpoint, {
      body: payload as RequestBodyJSON<TEndpoint>,
      params: args[0],
    })

    if (response.error) {
      const { response: httpResponse, error } =
        response as PutApiResponse<TEndpoint>
      this.handleErrorResponse(httpResponse, error as TErrorResponseData)

      return { error, response: httpResponse } as PutApiResponse<TEndpoint>
    }

    return response as PutApiResponse<TEndpoint>
  }

  public async patch<TEndpoint extends PatchEndpoints>(
    ...args: Parameters<typeof this.client.PATCH<TEndpoint>>
  ): Promise<PatchApiResponse<TEndpoint>> {
    const response = await this.client.PATCH<TEndpoint>(...args)

    if (response.error) {
      const { response: httpResponse, error } =
        response as PatchApiResponse<TEndpoint>
      this.handleErrorResponse(httpResponse, error as TErrorResponseData)

      return { error, response: httpResponse } as PatchApiResponse<TEndpoint>
    }

    return response
  }

  public async delete<TEndpoint extends DeleteEndpoints>(
    ...args: Parameters<typeof this.client.DELETE<TEndpoint>>
  ): Promise<DeleteApiResponse<TEndpoint>> {
    const response = await this.client.DELETE<TEndpoint>(...args)

    if (response.error) {
      const { response: httpResponse, error } =
        response as DeleteApiResponse<TEndpoint>
      this.handleErrorResponse(httpResponse, error as TErrorResponseData)

      return { error, response: httpResponse } as DeleteApiResponse<TEndpoint>
    }

    return response
  }

  private handleErrorResponse(
    response: Pick<Response, 'status'>,
    responseData: TErrorResponseData,
  ) {
    // Auto handle unauthorized by default
    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
      throw new UnauthorizedError(
        `${responseData.message}: Please reload the page and try again.`,
      )
    }

    if (
      response.status === HTTP_STATUS.FORBIDDEN &&
      responseData.message === 'JUSTIFICATION_REQUIRED'
    ) {
      throw new UserInfoAccessError(
        'Please provide justification for accessing this user information.',
      )
    }

    // Pass errorData to custom error handler
    this.onError(responseData)
    // If nothing is thrown, continue to handle other errors

    if (response.status === HTTP_STATUS.NOT_FOUND) {
      throw new NotFoundError(responseData.message)
    } else if (response.status === HTTP_STATUS.INTERNAL_ERROR) {
      throw new InternalServerError(responseData.message)
    } else {
      const messagePrefix = responseData?.message ?? 'An error occurred'

      // Only show the last error by default to avoid overwhelming the user
      // and to keep the error message concise.
      // Note: this is because it's a limitation on the BE where the root cause
      // is on the last position of the errors array.
      //
      // You can override this behavior by implementing `onError` in your service.
      let errorDetails = ''
      if (responseData?.errors?.at(-1)) {
        errorDetails = `: ${responseData.errors.at(-1)}`
      }

      let formattedErrorMessage = `${messagePrefix}${errorDetails}`
      // Add a full stop if it doesn't have one.
      if (formattedErrorMessage.at(-1) !== '.') {
        formattedErrorMessage += '.'
      }

      throw new Error(formattedErrorMessage)
    }
  }
}
